import {useMantineTheme} from '@mantine/core';
import type {MRT_ColumnDef} from 'mantine-react-table';
import {useMemo} from 'react';
import type {
  PersonnelSimpleResponse,
  UserStatusEnum,
} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {
  NoYesPills,
  UserBannedPill,
  UserIneligiblePill,
  UserLeaveOfAbsencePill,
  UserStatePills,
  UserSuspendedPill,
} from 'src/shared/constants/PillConstants';
import {ProfileAvatar} from 'src/app/personnel/ProfileAvatar';
import {IsPersonnelFlagged} from 'src/app/personnel/personnelHelpers';
import {
  UserStateDataOptions,
  YesNoStateDataOptions,
} from 'src/shared/constants/columnFilterData';
import {serverComputedColumn} from 'src/design/organisms/serverComputedColumn';

export const usePersonnelListColumns = () => {
  return useMemo<MRT_ColumnDef<PersonnelSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'avatarUrl',
        header: '',
        enableColumnFilterModes: false,
        enableColumnFilter: false,
        enableSorting: false,
        enableClickToCopy: false,
        enableColumnActions: false,
        enableResizing: false,
        size: 50,
        Cell: ({cell}) => (
          <ProfileAvatar alt={'Avatar Image'} src={cell.getValue<string>()} />
        ),
      },
      {
        accessorKey: 'active',
        header: 'Active',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: UserStateDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...UserStatePills(cell.getValue<UserStatusEnum>())} />
        ),
      },
      serverComputedColumn({
        accessorKey: 'hasOpenTraining',
        header: 'Open Training',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: YesNoStateDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...NoYesPills(cell.getValue<boolean | undefined>())} />
        ),
        filterable: true,
      }),
      {
        accessorKey: 'vendor.id',
        header: 'Vendor ID',
      },
      {
        accessorKey: 'vendor.name',
        header: 'Vendor',
      },
      {
        accessorKey: 'firstName',
        header: 'First',
      },
      {
        accessorKey: 'lastName',
        header: 'Last',
      },
      {
        accessorKey: 'employeeId',
        header: 'Employee ID',
      },
      {
        accessorKey: 'email',
        header: 'Email',
      },
      {
        accessorKey: 'supervisor.name',
        header: 'Supervisor',
      },
      {
        accessorKey: 'isSuspended',
        header: 'Suspended',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: YesNoStateDataOptions,
        },
        maxSize: 150,
        Cell: ({cell}) => (
          <>
            {cell.getValue<boolean | undefined>() && (
              <Pill {...UserSuspendedPill} />
            )}
          </>
        ),
      },
      {
        accessorKey: 'isLeaveOfAbsence',
        header: 'LOA',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: YesNoStateDataOptions,
        },
        maxSize: 150,
        Cell: ({cell}) => (
          <>
            {cell.getValue<boolean | undefined>() && (
              <Pill {...UserLeaveOfAbsencePill} />
            )}
          </>
        ),
      },
      {
        accessorKey: 'isEligible',
        header: 'Eligibility',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: YesNoStateDataOptions,
        },
        maxSize: 150,
        Cell: ({cell}) => (
          <>
            {cell.getValue<boolean | undefined>() === false && (
              <Pill {...UserIneligiblePill} />
            )}
          </>
        ),
      },
      {
        accessorKey: 'isBannedOnProperty',
        header: 'Banned on Property',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: YesNoStateDataOptions,
        },
        maxSize: 150,
        Cell: ({cell}) => (
          <>
            {cell.getValue<boolean | undefined>() && (
              <Pill {...UserBannedPill} />
            )}
          </>
        ),
      },
      {
        accessorKey: 'jobCode',
        header: 'Job Code',
      },
      {
        accessorKey: 'jobTitle',
        header: 'Job Title',
      },
    ],
    []
  );
};

export const usePersonnelListRowStyles = () => {
  const theme = useMantineTheme();

  return (row: PersonnelSimpleResponse) => ({
    backgroundColor: IsPersonnelFlagged(row) ? theme.colors.orange[1] : 'white',
  });
};
